import { z } from 'zod';
import { paginationInputSchema, type SortDirection, sortInput } from './pagination.js';

/**
 * The free text a list screen's search box sends. One name across every list,
 * because a filter called `search` on one route and `q` on the next is a second
 * convention for the same input.
 */
export const listSearchSchema = z.string().trim().max(200).optional();

/**
 * What every list endpoint takes before its own filters: the page, its size and
 * the free text. A route adds its typed filter map with `.extend({ … })`, and
 * one that offers no free text says so with `.omit({ search: true })` rather
 * than accepting an input it ignores.
 */
export const listRequestSchema = paginationInputSchema.extend({ search: listSearchSchema });

export type ListRequest = z.infer<typeof listRequestSchema>;

/**
 * The same, sorted. `sortInput` narrows `sortBy` to the resource's own columns,
 * so a column the client invents is a 400 at the boundary and never reaches a
 * repository — docs/decisions/0039-the-sort-whitelist-and-the-tiebreaker.md.
 */
export function sortedListRequest<const Fields extends readonly [string, ...string[]]>(
  fields: Fields,
  defaultField: Fields[number],
  defaultDirection: SortDirection,
) {
  return listRequestSchema.extend(sortInput(fields, defaultField, defaultDirection).shape);
}

/**
 * One row of a bulk answer: the id the request named, whether it moved, and a
 * stable English `code` naming why it did not. `null` is a plain success.
 *
 * The code is an identifier the client maps to Turkish, never a rendered
 * sentence: a message built on the server would be English in a Turkish screen
 * — .claude/rules/i18n-turkish-ui.md. That is the one departure from this
 * item's original wording in docs/plans/04-donations.md §4.2, which asked for
 * `failed: { id, code, message }[]`; the split it wanted is `partitionBulk`
 * below, computed by the client from the rows.
 */
export function bulkRowSchema<const Codes extends readonly [string, ...string[]]>(codes: Codes) {
  return z.object({
    id: z.uuid(),
    ok: z.boolean(),
    code: z.enum(codes).nullable(),
  });
}

/**
 * The envelope every bulk endpoint answers with: one result per row, **in
 * request order**, so a screen can name exactly which of the rows a person
 * selected was refused. A route whose rows carry more than the three columns
 * extends the row schema rather than defining a second envelope.
 */
export function bulkResultSchema<Row extends z.ZodType>(row: Row) {
  return z.object({ results: z.array(row) });
}

export type BulkRow = { id: string; ok: boolean; code: string | null };

export type BulkPartition = {
  succeeded: string[];
  failed: { id: string; code: string }[];
};

/**
 * The `succeeded` / `failed` split a screen reports from, derived from the rows
 * rather than sent twice. A refused row with no code is reported under
 * `UNKNOWN`, because a failure the client cannot name is still a failure.
 */
export function partitionBulk(results: readonly BulkRow[]): BulkPartition {
  const succeeded: string[] = [];
  const failed: { id: string; code: string }[] = [];
  for (const row of results) {
    if (row.ok) succeeded.push(row.id);
    else failed.push({ id: row.id, code: row.code ?? 'UNKNOWN' });
  }
  return { succeeded, failed };
}
